import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import { format } from 'date-fns';
import installBaseService from '../../services/installBase.service';
import { inventoryService } from '../../services/inventory.service';
import { apiService } from '../../services/api.service';
import { useParams } from 'react-router-dom';
import { Warning, CheckCircle, OpenInNew as OpenInNewIcon, Refresh as RefreshIcon, Info as InfoIcon, Error as ErrorIcon, Schedule as ScheduleIcon, TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon, Search as SearchIcon, Clear as ClearIcon, ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InstallBaseBreadcrumbs from './InstallBaseBreadcrumbs';
import EquipmentSpecsDialog from './dialogs/EquipmentSpecsDialog';
import ServiceRequestDialog from './dialogs/ServiceRequestDialog';
import { debounce } from 'lodash';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`installation-tabpanel-${index}`}
      aria-labelledby={`installation-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return format(date, 'PPP');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  const num = Number(value);
  if (isNaN(num)) return '$0.00';
  return `$${num.toFixed(2)}`;
};

const calculateTotal = (quantity, unitCost) => {
  const qty = Number(quantity) || 0;
  const cost = Number(unitCost) || 0;
  return formatCurrency(qty * cost);
};

// Add utility functions for sorting
function descendingComparator(a, b, orderBy) {
  // Custom sorting for severity
  if (orderBy === 'severity') {
    const severityOrder = { high: 1, medium: 2, low: 3 };
    const aValue = severityOrder[a[orderBy]] || 0;
    const bValue = severityOrder[b[orderBy]] || 0;
    return bValue - aValue;
  }

  // Default sorting for other fields
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const stockStatusOptions = [
  { value: '', label: 'All' },
  { value: 'In Stock', label: 'In Stock' },
  { value: 'Partial Stock', label: 'Partial Stock' },
  { value: 'Out of Stock', label: 'Out of Stock' }
];

export default function InstallationDetails() {
  const { installationId } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [summaryData, setSummaryData] = useState(null);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [bomDetails, setBomDetails] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inventoryData, setInventoryData] = useState({});
  const [inventoryLoading, setInventoryLoading] = useState({});
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [specsDialogOpen, setSpecsDialogOpen] = useState(false);
  const [selectedSpecs, setSelectedSpecs] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [selectedAlertId, setSelectedAlertId] = useState(null);

  // Get unique values for category filter
  const uniqueCategories = useMemo(() => {
    const categories = new Set(bomDetails.map(item => item.item_category));
    return Array.from(categories).filter(Boolean);
  }, [bomDetails]);

  // Fetch alerts when installationId changes
  useEffect(() => {
    const fetchAlerts = async () => {
      if (!installationId) return;
      
      try {
        const response = await installBaseService.getAlertsByInstallation(installationId);
        // Transform the API response to match the expected format
        const transformedAlerts = response.map(alert => ({
          id: alert.alert_id,
          type: alert.alert_type,
          category: alert.alert_category,
          title: alert.title,
          description: alert.description,
          severity: alert.alert_severity,
          date: new Date(alert.alert_date),
          dueDate: alert.due_date ? new Date(alert.due_date) : null,
          action: alert.alert_action,
          status: alert.alert_status
        }));
        setAlerts(transformedAlerts);
      } catch (err) {
        console.error('Error fetching alerts:', err);
        setError('Failed to load alerts');
      }
    };

    fetchAlerts();
  }, [installationId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch installation details
        const installationData = await apiService.get(`/install-base/installations/${installationId}`);
        console.log('Installation Data:', installationData);
        
        if (!installationData) {
          throw new Error('No installation data found');
        }

        // Fetch location details
        const locationData = await apiService.get(`/install-base/locations/bylocationid/${installationData.location_id}`);
        console.log('Location Data:', locationData);

        // Fetch customer details
        const customerData = await apiService.get(`/install-base/customers/${locationData.customer_id}`);
        console.log('Customer Data:', customerData);

        // Fetch equipment model details
        const modelData = await apiService.get(`/install-base/equipment-models/${installationData.model_id}`);
        console.log('Model Data:', modelData);

        setSummaryData({
          serialNumber: installationData.serial_number,
          installDate: installationData.install_date,
          warrantyExpiryDate: installationData.warranty_expiry_date,
          status: installationData.status,
          customer: customerData ? {
            name: customerData.name,
            id: customerData.customer_id,
            industry: customerData.industry,
            contactName: customerData.contact_name,
            contactEmail: customerData.contact_email,
            contactPhone: customerData.contact_phone
          } : null,
          location: locationData ? {
            name: locationData.name,
            id: locationData.location_id,
            address: locationData.address,
            city: locationData.city,
            state: locationData.state,
            country: locationData.country
          } : null,
          equipmentModel: modelData ? {
            modelName: modelData.model_name,
            manufacturer: modelData.manufacturer,
            category: modelData.category,
            serviceLifeYears: modelData.service_life_years,
            specDetail: modelData.spec_detail
          } : null
        });

        // Fetch BOM details
        const bomResponse = await apiService.get(`/bom/details/with-stocks?installation_id=${installationId}`);
        setBomDetails(bomResponse);

        // Fetch service events with the new endpoint
        const serviceEventsResponse = await apiService.get(
          `/install-base/installations/${installationId}/service-events`
        );
        console.log('Service Events Response:', serviceEventsResponse);
        setServiceHistory(serviceEventsResponse);

      } catch (err) {
        console.error('Error fetching installation details:', err);
        setError(err.message || 'Failed to fetch installation details');
      } finally {
        setLoading(false);
      }
    };

    if (installationId) {
      fetchData();
    }
  }, [installationId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchInventoryData = useCallback(async (sku) => {
    if (inventoryLoading[sku]) return;
    
    setInventoryLoading(prev => ({ ...prev, [sku]: true }));
    
    try {
      console.log(`Fetching inventory data for SKU: ${sku}`);
      // Fetch real data from API
      const response = await inventoryService.getSupplierInventory(sku);
      console.log('API Response:', response);
      
      if (response?.inventory_data) {
        const { inventory_data } = response.inventory_data;
        
        // Transform the data to match our format
        const transformedData = {
          total_available: response.inventory_data.total_available || 0,
          total_in_transit: response.inventory_data.total_in_transit || 0,
          total_ordered: response.inventory_data.total_ordered || 0,
          supplier_count: response.inventory_data.supplier_count || 0,
          inventory_data: inventory_data.map(supplier => ({
            supplier_name: supplier.supplier_name,
            available_quantity: supplier.available_quantity || 0,
            in_transit_quantity: supplier.in_transit_quantity || 0,
            ordered_quantity: supplier.ordered_quantity || 0
          }))
        };

        console.log('Transformed Data:', transformedData);
        setInventoryData(prev => ({
          ...prev,
          [sku]: transformedData
        }));
      } else {
        console.warn('Invalid response format:', response);
        setInventoryData(prev => ({
          ...prev,
          [sku]: {
            error: true,
            errorMessage: 'Invalid response format'
          }
        }));
      }
    } catch (error) {
      console.error(`Error fetching inventory data for ${sku}:`, error);
      setInventoryData(prev => ({
        ...prev,
        [sku]: {
          error: true,
          errorMessage: error.message || 'Failed to fetch inventory data'
        }
      }));
    } finally {
      setInventoryLoading(prev => ({ ...prev, [sku]: false }));
    }
  }, []);

  const handleSpecsClick = (specs) => {
    setSelectedSpecs(specs);
    setSpecsDialogOpen(true);
  };

  const handleSpecsDialogClose = () => {
    setSpecsDialogOpen(false);
    setSelectedSpecs(null);
  };

  const handleOpenServiceDialog = (alertId) => {
    setSelectedAlertId(alertId);
    setServiceDialogOpen(true);
  };

  const handleCloseServiceDialog = () => {
    setServiceDialogOpen(false);
    setSelectedAlertId(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const SummarySection = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Customer Information</Typography>
          {summaryData?.customer && (
            <>
              <Typography><strong>Name:</strong> {summaryData.customer.name}</Typography>
              <Typography><strong>Industry:</strong> {summaryData.customer.industry}</Typography>
              <Typography><strong>Contact:</strong> {summaryData.customer.contactName}</Typography>
              <Typography><strong>Email:</strong> {summaryData.customer.contactEmail}</Typography>
              <Typography><strong>Phone:</strong> {summaryData.customer.contactPhone}</Typography>
            </>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Location Details</Typography>
          {summaryData?.location && (
            <>
              <Typography><strong>Name:</strong> {summaryData.location.name}</Typography>
              <Typography><strong>Address:</strong> {summaryData.location.address}</Typography>
              <Typography><strong>City:</strong> {summaryData.location.city}</Typography>
              <Typography><strong>State:</strong> {summaryData.location.state}</Typography>
              <Typography><strong>Country:</strong> {summaryData.location.country}</Typography>
            </>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Installation Details</Typography>
          {summaryData && (
            <>
              <Typography><strong>Serial Number:</strong> {summaryData.serialNumber}</Typography>
              <Typography><strong>Install Date:</strong> {formatDate(summaryData.installDate)}</Typography>
              <Typography><strong>Warranty Expiry:</strong> {formatDate(summaryData.warrantyExpiryDate)}</Typography>
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <strong>Status:</strong> <Chip label={summaryData.status} color="primary" sx={{ ml: 1 }} />
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  href="/OpenBlue.webp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Telemetry Details
                </Button>
              </Typography>
            </>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Equipment Details</Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography><strong>Model:</strong> {summaryData.equipmentModel.modelName}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography><strong>Manufacturer:</strong> {summaryData.equipmentModel.manufacturer}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography><strong>Category:</strong> {summaryData.equipmentModel.category}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography><strong>Service Life:</strong> {summaryData.equipmentModel.serviceLifeYears} years</Typography>
            </Grid>
            {summaryData.equipmentModel.specDetail && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => handleSpecsClick(summaryData.equipmentModel.specDetail)}
                  sx={{ mt: 1, borderWidth: 2 }}
                >
                  Show Specifications
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );

  const ServiceHistorySection = () => (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Service History</Typography>
      {serviceHistory.length > 0 ? (
        <Timeline sx={{ 
          '& .MuiTimelineItem-root:before': {
            flex: 0,
            padding: 0,
          },
          '& .MuiTimelineOppositeContent-root': {
            flex: 0.2,
            padding: '6px 16px',
          },
          '& .MuiTimelineContent-root': {
            flex: 0.8,
            padding: '6px 16px',
          }
        }}>
          {serviceHistory.map((event) => (
            <TimelineItem key={event.service_event_id}>
              <TimelineOppositeContent color="text.secondary" sx={{ minWidth: '100px' }}>
                {formatDate(event.event_date)}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot color={event.is_emergency ? "error" : "primary"}>
                  {event.is_emergency ? <Warning /> : <CheckCircle />}
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" component="span">
                      {event.service_type_name}
                    </Typography>
                    <Chip 
                      label={event.status} 
                      color="primary" 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                    {event.is_scheduled && (
                      <Chip 
                        label="Scheduled" 
                        color="info" 
                        size="small" 
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Technician: {event.technician_name}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {event.description}
                  </Typography>
                  {event.resolution && (
                    <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                      Resolution: {event.resolution}
                    </Typography>
                  )}
                  {event.part_replacements && event.part_replacements.length > 0 && (
                    <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2">Part Replacements:</Typography>
                      {event.part_replacements.map(part => (
                        <Box key={part.part_replacement_id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                          <Typography variant="body2">
                            {part.part_name} ({part.part_number}) - {part.quantity} {part.uom}
                          </Typography>
                          <Chip 
                            size="small" 
                            label={formatCurrency(part.total_cost)} 
                            color="secondary"
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                  {event.documents && event.documents.length > 0 && (
                    <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2">Documents:</Typography>
                      {event.documents.map(doc => (
                        <Box key={doc.document_id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                          <Typography variant="body2">
                            {doc.document_title}
                          </Typography>
                          <Chip 
                            size="small" 
                            label={doc.document_type} 
                            color="info"
                          />
                          <IconButton 
                            size="small" 
                            onClick={() => window.open(doc.document_url, '_blank')}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      ) : (
        <Typography align="center">No service history available</Typography>
      )}
    </Paper>
  );

  const BOMDetailsSection = () => {
    // Utility functions
    const getStockStatus = (available, target) => {
      if (available >= target) return 'In Stock';
      if (available > 0) return 'Partial Stock';
      return 'Out of Stock';
    };

    const getStockStatusColor = (status) => {
      switch (status) {
        case 'In Stock':
          return 'success';
        case 'Partial Stock':
          return 'warning';
        case 'Out of Stock':
          return 'error';
        default:
          return 'default';
      }
    };

    // Define columns
    const columns = [
      { id: 'model_part_no', label: 'Part Number', minWidth: 100, filterable: true },
      { id: 'item_description', label: 'Part Name', minWidth: 170, filterable: true },
      { id: 'item_category', label: 'Category', minWidth: 100, filterable: true },
      { id: 'quantity_required', label: 'Quantity', minWidth: 100, align: 'right' },
      { id: 'uom', label: 'UOM', minWidth: 100 },
      { id: 'actions', label: 'Actions', minWidth: 100, align: 'center' }
    ];

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('model_part_no');
    const [filters, setFilters] = useState({
      model_part_no: '',
      item_description: '',
      item_category: '',
      stock_status: ''
    });
    const [expandedCategories, setExpandedCategories] = useState({});
    const [inventoryData, setInventoryData] = useState({});
    const [inventoryLoading, setInventoryLoading] = useState({});
    const [editingRow, setEditingRow] = useState(null);
    const [editedValues, setEditedValues] = useState({});

    // Simplified filtering logic
    const filteredData = useMemo(() => {
      const searchTerm = filters.model_part_no.toLowerCase();
      const descriptionTerm = filters.item_description.toLowerCase();
      const categoryFilter = filters.item_category;
      const stockStatusFilter = filters.stock_status;

      return bomDetails.filter(item => {
        const matchesSearch = !searchTerm || item.model_part_no?.toLowerCase().includes(searchTerm);
        const matchesDescription = !descriptionTerm || item.item_description?.toLowerCase().includes(descriptionTerm);
        const matchesCategory = !categoryFilter || item.item_category === categoryFilter;
        const stockStatus = getStockStatus(item.available_quantity, item.target_quantity);
        const matchesStockStatus = !stockStatusFilter || stockStatus === stockStatusFilter;

        return matchesSearch && matchesDescription && matchesCategory && matchesStockStatus;
      });
    }, [bomDetails, filters]);

    // Simplified grouping logic
    const groupedData = useMemo(() => {
      return filteredData.reduce((groups, item) => {
        const category = item.item_category || 'Uncategorized';
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(item);
        return groups;
      }, {});
    }, [filteredData]);

    // Initialize expanded categories
    useEffect(() => {
      const initialExpanded = {};
      Object.keys(groupedData).forEach(category => {
        initialExpanded[category] = true;
      });
      setExpandedCategories(initialExpanded);
    }, [groupedData]);

    const fetchInventoryData = useCallback(async (sku) => {
      if (inventoryLoading[sku]) return;
      
      setInventoryLoading(prev => ({ ...prev, [sku]: true }));
      
      try {
        const response = await inventoryService.getSupplierInventory(sku);
        
        if (response?.inventory_data) {
          const { inventory_data } = response.inventory_data;
          
          const transformedData = {
            total_available: response.inventory_data.total_available || 0,
            total_in_transit: response.inventory_data.total_in_transit || 0,
            total_ordered: response.inventory_data.total_ordered || 0,
            supplier_count: response.inventory_data.supplier_count || 0,
            inventory_data: inventory_data.map(supplier => ({
              supplier_name: supplier.supplier_name,
              available_quantity: supplier.available_quantity || 0,
              in_transit_quantity: supplier.in_transit_quantity || 0,
              ordered_quantity: supplier.ordered_quantity || 0
            }))
          };

          setInventoryData(prev => ({
            ...prev,
            [sku]: transformedData
          }));
        }
      } catch (error) {
        setInventoryData(prev => ({
          ...prev,
          [sku]: {
            error: true,
            errorMessage: error.message || 'Failed to fetch inventory data'
          }
        }));
      } finally {
        setInventoryLoading(prev => ({ ...prev, [sku]: false }));
      }
    }, []);

    const handleFilterChange = useCallback((field, value) => {
      setFilters(prev => ({
        ...prev,
        [field]: value
      }));
    }, []);

    const clearFilters = useCallback(() => {
      setFilters({
        model_part_no: '',
        item_description: '',
        item_category: '',
        stock_status: ''
      });
    }, []);

    const toggleCategory = useCallback((category) => {
      setExpandedCategories(prev => ({
        ...prev,
        [category]: !prev[category]
      }));
    }, []);

    const handleRequestSort = useCallback((property) => {
      setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }, []);

    const handleEditClick = (row) => {
      setEditingRow(row.bom_item_id);
      setEditedValues({
        model_part_no: row.model_part_no,
        item_description: row.item_description,
        item_category: row.item_category,
        quantity_required: row.quantity_required,
        uom: row.uom
      });
    };

    const handleCancelEdit = () => {
      setEditingRow(null);
      setEditedValues({});
    };

    const handleSaveEdit = () => {
      // Here you would typically make an API call to save the changes
      console.log('Saving changes:', editedValues);
      setEditingRow(null);
      setEditedValues({});
    };

    const handleInputChange = (field, value) => {
      setEditedValues(prev => ({
        ...prev,
        [field]: value
      }));
    };

    return (
      <Box>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Bill of Materials
            </Typography>
            {bomDetails.length > 0 && bomDetails[0].bom_version && (
              <Chip 
                label={`Version ${bomDetails[0].bom_version}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <TextField
            size="small"
            label="Part Number"
            value={filters.model_part_no}
            onChange={(e) => handleFilterChange('model_part_no', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: filters.model_part_no && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => handleFilterChange('model_part_no', '')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            size="small"
            label="Part Name"
            value={filters.item_description}
            onChange={(e) => handleFilterChange('item_description', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: filters.item_description && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => handleFilterChange('item_description', '')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.item_category}
              label="Category"
              onChange={(e) => handleFilterChange('item_category', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {uniqueCategories.map(category => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Stock Status</InputLabel>
            <Select
              value={filters.stock_status}
              label="Stock Status"
              onChange={(e) => handleFilterChange('stock_status', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="In Stock">In Stock</MenuItem>
              <MenuItem value="Partial Stock">Partial Stock</MenuItem>
              <MenuItem value="Out of Stock">Out of Stock</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={clearFilters}
            startIcon={<ClearIcon />}
          >
            Clear Filters
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.50',
                '& .MuiTableCell-head': {
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  py: 1.5
                }
              }}>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(groupedData).length > 0 ? (
                Object.entries(groupedData).map(([category, items]) => (
                  <React.Fragment key={category}>
                    <TableRow
                      onClick={() => toggleCategory(category)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: 'grey.100',
                        '&:hover': {
                          bgcolor: 'grey.200',
                        }
                      }}
                    >
                      <TableCell colSpan={columns.length}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {expandedCategories[category] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {category} ({items.length} items)
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {expandedCategories[category] && items.map((part) => {
                      const inventory = inventoryData[part.model_part_no];
                      const isLoading = inventoryLoading[part.model_part_no];
                      const stockStatus = getStockStatus(part.available_quantity, part.target_quantity);
                      
                      return (
                        <TableRow 
                          key={part.bom_item_id}
                          sx={{
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                            '& .MuiTableCell-body': {
                              py: 1.25
                            }
                          }}
                        >
                          {columns.map((column) => {
                            const value = column.id === 'model_part_no' ? (
                              editingRow === part.bom_item_id ? (
                                <TextField
                                  size="small"
                                  value={editedValues.model_part_no}
                                  onChange={(e) => handleInputChange('model_part_no', e.target.value)}
                                  fullWidth
                                />
                              ) : part.model_part_no
                            ) : column.id === 'item_description' ? (
                              editingRow === part.bom_item_id ? (
                                <TextField
                                  size="small"
                                  value={editedValues.item_description}
                                  onChange={(e) => handleInputChange('item_description', e.target.value)}
                                  fullWidth
                                />
                              ) : part.item_description
                            ) : column.id === 'item_category' ? (
                              editingRow === part.bom_item_id ? (
                                <TextField
                                  size="small"
                                  value={editedValues.item_category}
                                  onChange={(e) => handleInputChange('item_category', e.target.value)}
                                  fullWidth
                                />
                              ) : part.item_category
                            ) : column.id === 'quantity_required' ? (
                              editingRow === part.bom_item_id ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editedValues.quantity_required}
                                  onChange={(e) => handleInputChange('quantity_required', e.target.value)}
                                  fullWidth
                                />
                              ) : part.quantity_required
                            ) : column.id === 'uom' ? (
                              editingRow === part.bom_item_id ? (
                                <TextField
                                  size="small"
                                  value={editedValues.uom}
                                  onChange={(e) => handleInputChange('uom', e.target.value)}
                                  fullWidth
                                />
                              ) : part.uom
                            ) : column.id === 'actions' ? (
                              editingRow === part.bom_item_id ? (
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={handleSaveEdit}
                                  >
                                    <SaveIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={handleCancelEdit}
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Box>
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditClick(part)}
                                >
                                  <EditIcon />
                                </IconButton>
                              )
                            ) : null;
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {value}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">No matching BOM details found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const AlertsSection = () => {
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('severity');
    const [filters, setFilters] = useState({
      type: '',
      title: '',
      category: '',
      severity: '',
      status: ''
    });

    // Get unique values for filters
    const uniqueTypes = [...new Set(alerts.map(alert => alert.type))].filter(Boolean);
    const uniqueCategories = [...new Set(alerts.map(alert => alert.category))].filter(Boolean);
    const uniqueSeverities = [...new Set(alerts.map(alert => alert.severity))].filter(Boolean);
    const uniqueStatuses = [...new Set(alerts.map(alert => alert.status))].filter(Boolean);

    const handleRequestSort = (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    };

    const createSortHandler = (property) => (event) => {
      handleRequestSort(property);
    };

    const handleFilterChange = (field, value) => {
      setFilters(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const clearFilters = () => {
      setFilters({
        type: '',
        title: '',
        category: '',
        severity: '',
        status: ''
      });
    };

    const filteredData = alerts.filter(alert => {
      return (
        (filters.type === '' || alert.type === filters.type) &&
        alert.title.toLowerCase().includes(filters.title.toLowerCase()) &&
        (filters.category === '' || alert.category === filters.category) &&
        (filters.severity === '' || alert.severity === filters.severity) &&
        (filters.status === '' || alert.status === filters.status)
      );
    });

    const headCells = [
      { id: 'type', label: 'Type', numeric: false, filterable: true },
      { id: 'title', label: 'Alert', numeric: false, filterable: true },
      { id: 'description', label: 'Description', numeric: false, filterable: false },
      { id: 'severity', label: 'Severity', numeric: false, filterable: true },
      { id: 'date', label: 'Date', numeric: false, filterable: false },
      { id: 'status', label: 'Status', numeric: false, filterable: true },
      { id: 'create_request', label: 'Create Request', numeric: false, filterable: false }
    ];

    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2,
          p: 1,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              System Alerts
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
            >
              Clear Filters
            </Button>
            <Chip 
              icon={<ScheduleIcon />} 
              label="Predictive" 
              color="info" 
              variant="outlined" 
              size="small" 
            />
            <Chip 
              icon={<ErrorIcon />} 
              label="Reactive" 
              color="error" 
              variant="outlined" 
              size="small" 
            />
          </Box>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.50',
                '& .MuiTableCell-head': {
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  py: 1.5
                }
              }}>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={createSortHandler(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={`filter-${headCell.id}`}
                    align={headCell.numeric ? 'right' : 'left'}
                    sx={{ py: 1 }}
                  >
                    {headCell.filterable && (
                      headCell.id === 'type' ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={filters[headCell.id]}
                            onChange={(e) => handleFilterChange(headCell.id, e.target.value)}
                            displayEmpty
                            size="small"
                          >
                            <MenuItem value="">All Types</MenuItem>
                            {uniqueTypes.map(type => (
                              <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : headCell.id === 'category' ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={filters[headCell.id]}
                            onChange={(e) => handleFilterChange(headCell.id, e.target.value)}
                            displayEmpty
                            size="small"
                          >
                            <MenuItem value="">All Categories</MenuItem>
                            {uniqueCategories.map(category => (
                              <MenuItem key={category} value={category}>{category}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : headCell.id === 'severity' ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={filters[headCell.id]}
                            onChange={(e) => handleFilterChange(headCell.id, e.target.value)}
                            displayEmpty
                            size="small"
                          >
                            <MenuItem value="">All Severities</MenuItem>
                            {uniqueSeverities.map(severity => (
                              <MenuItem key={severity} value={severity}>{severity}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : headCell.id === 'status' ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={filters[headCell.id]}
                            onChange={(e) => handleFilterChange(headCell.id, e.target.value)}
                            displayEmpty
                            size="small"
                          >
                            <MenuItem value="">All Statuses</MenuItem>
                            {uniqueStatuses.map(status => (
                              <MenuItem key={status} value={status}>{status}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          size="small"
                          value={filters[headCell.id]}
                          onChange={(e) => handleFilterChange(headCell.id, e.target.value)}
                          placeholder={`Filter ${headCell.label}`}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        />
                      )
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData
                  .slice()
                  .sort(getComparator(order, orderBy))
                  .map((alert) => {
                    const isPredictive = alert.type === 'predictive';
                    const severityColor = {
                      high: 'error.main',
                      medium: 'warning.main',
                      low: 'info.main'
                    }[alert.severity];

                    return (
                      <TableRow 
                        key={alert.id}
                        sx={{
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                          '& .MuiTableCell-body': {
                            py: 1.25
                          }
                        }}
                      >
                        <TableCell>
                          <Chip
                            icon={isPredictive ? <ScheduleIcon /> : <ErrorIcon />}
                            label={isPredictive ? 'Predictive' : 'Reactive'}
                            color={isPredictive ? 'info' : 'error'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {alert.title}
                            </Typography>
                            {alert.category === 'temperature' && <TrendingUpIcon color="error" fontSize="small" />}
                            {alert.category === 'pressure' && <TrendingDownIcon color="error" fontSize="small" />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {alert.description}
                            </Typography>
                            {isPredictive && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Due: {format(alert.dueDate, 'MMM d, yyyy')}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={alert.severity}
                            size="small"
                            sx={{
                              bgcolor: severityColor,
                              color: 'white',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(alert.date, 'MMM d, HH:mm')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={alert.status}
                            size="small"
                            color={alert.status === 'active' ? 'error' : 'warning'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOpenServiceDialog(alert.id)}
                          >
                            Request Service
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center">No matching alerts found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <InstallBaseBreadcrumbs
        customerName={summaryData?.customer?.name}
        customerId={summaryData?.customer?.id}
        locationName={summaryData?.location?.name}
        locationId={summaryData?.location?.id}
        installationName={summaryData?.serialNumber}
        installationId={installationId}
      />
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>Installation Summary</Typography>
        <SummarySection />
      </Box>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            aria-label="installation details tabs"
          >
            <Tab label="Alerts" id="installation-tab-0" aria-controls="installation-tabpanel-0" />
            <Tab label="Service History" id="installation-tab-1" aria-controls="installation-tabpanel-1" />
            <Tab label="BOM Details" id="installation-tab-2" aria-controls="installation-tabpanel-2" />
          </Tabs>
        </Box>
        <TabPanel value={tabValue} index={0}>
          <AlertsSection />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <ServiceHistorySection />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <BOMDetailsSection />
        </TabPanel>
      </Box>
      <EquipmentSpecsDialog
        open={specsDialogOpen}
        onClose={handleSpecsDialogClose}
        specs={selectedSpecs}
      />
      <ServiceRequestDialog
        open={serviceDialogOpen}
        onClose={handleCloseServiceDialog}
        installationId={installationId}
        alertId={selectedAlertId}
        customerName={summaryData?.customer?.name}
        locationName={summaryData?.location?.name}
        serialNumber={summaryData?.serialNumber}
        requesterName={summaryData?.customer?.contactName}
      />
    </Box>
  );
} 